import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { MapPin, Users, Lock, BadgeCheck, Globe, Check } from "lucide-react";
import { WomenCommunityCard } from "@/components/community/WomenCommunityCard";
import { SuggestCommunity } from "@/components/community/SuggestCommunity";
import { plural } from "@/lib/utils";
import { useShowMemberCounts } from "@/hooks/useCommunityConfig";

function GeneralCard({ g, t }) {
  const showCounts = useShowMemberCounts();
  const my = g.my_membership;
  const isMember = my?.status === "active";
  return (
    <Link to={`/comunidade/${g.id}`} data-testid="community-general-card" className="block bg-[#0d0d0d] text-white rounded-2xl p-4 card-hover mb-6 relative overflow-hidden">
      <div className="auk-ribbon absolute top-0 left-0 right-0 h-1" />
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#c8102e] flex items-center justify-center shrink-0 overflow-hidden">
          {g.cover_url ? <img src={g.cover_url} alt="" className="w-full h-full object-cover" /> : <Globe size={22} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <div className="font-display font-bold text-base leading-tight" data-testid="community-general-name">{g.name}</div>
            <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/15 text-[#ffd100]" data-testid="community-general-official"><BadgeCheck size={10} /> Oficial</span>
          </div>
          <div className="text-[11px] text-white/60 flex items-center gap-2 flex-wrap mb-1">
            <span className="inline-flex items-center gap-0.5" data-testid="community-general-scope"><Globe size={10} /> Nacional · Reino Unido</span>
            {showCounts && <span data-testid="community-general-members"><Users size={11} className="inline mr-0.5" />{plural(g.members_count, t("member_one"), t("member_other"))}</span>}
          </div>
          <p className="text-xs text-white/70 leading-relaxed line-clamp-2">{g.description}</p>
          {isMember && <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-white/90" data-testid="community-general-member-badge"><Check size={12} /> Já és membro</span>}
          {!isMember && <span className="mt-2 inline-block text-[11px] font-semibold text-[#ffd100]" data-testid="community-general-join-hint">Aberta a todos os membros · Entrar</span>}
        </div>
      </div>
    </Link>
  );
}

function CityCard({ c, onFilter, t }) {
  const showCounts = useShowMemberCounts();
  const inner = (
    <>
      <div className="flex items-center gap-1 text-xs text-zinc-500 mb-1"><MapPin size={11} /> {c.region}</div>
      <div className="font-display font-bold text-lg">{c.name}</div>
      {c.community_id
        ? <div className="text-xs text-zinc-600 flex items-center gap-1 mt-1 flex-wrap" data-testid={`city-members-${c.name}`}>{showCounts && <><Users size={11} /> {plural(c.members_count, t("member_one"), t("member_other"))}</>}<span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-[#c8102e]" data-testid={`city-official-${c.name}`}><BadgeCheck size={10} /> Oficial</span></div>
        : <div className="text-xs text-zinc-400 italic mt-1" data-testid={`city-soon-${c.name}`}>Em breve</div>}
    </>
  );
  const cls = "bg-white border border-zinc-200 rounded-2xl p-4 text-left card-hover block";
  if (c.community_id) return <Link to={`/comunidade/${c.community_id}`} data-testid={`community-region-${c.name}`} className={cls}>{inner}</Link>;
  return <button type="button" onClick={() => onFilter(c.name)} data-testid={`community-region-${c.name}`} className={cls}>{inner}</button>;
}

export default function Community() {
  const { t } = useLang();
  const showCounts = useShowMemberCounts();
  const [params, setParams] = useSearchParams();
  const cityParam = params.get("city") || "";
  const [cities, setCities] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [general, setGeneral] = useState(null);
  const [women, setWomen] = useState(null);
  const [config, setConfig] = useState({ user_groups_enabled: false });
  const [q, setQ] = useState(cityParam);

  const loadWomen = () => api.get("/communities/by-slug/angolanas-em-uk").then(r => setWomen(r.data)).catch(() => setWomen(null));
  useEffect(() => {
    api.get("/cities").then(r => setCities(r.data));
    api.get("/communities/general").then(r => setGeneral(r.data)).catch(() => setGeneral(null));
    api.get("/communities/config").then(r => setConfig(r.data)).catch(() => {});
    loadWomen();
  }, []);

  useEffect(() => {
    api.get("/communities", { params: { official: false, kind: "community", ...(cityParam ? { city: cityParam } : {}) } })
       .then(r => setCommunities(r.data.filter(g => g.slug !== "angolanas-em-uk")));
  }, [cityParam]);

  const filteredCities = cities.filter(c =>
    !q || c.name.toLowerCase().includes(q.toLowerCase()) || c.region.toLowerCase().includes(q.toLowerCase())
  );

  const setCity = (city) => {
    const p = new URLSearchParams(params);
    if (city) p.set("city", city); else p.delete("city");
    setParams(p);
  };

  return (
    <div className="page fade-up">
      <div className="pt-4 pb-2">
        <h1 className="font-display text-2xl font-extrabold mb-1" data-testid="communities-title">Comunidades</h1>
        <p className="text-sm text-zinc-500 mb-4">A Comunidade Geral, as comunidades da tua cidade e os espaços oficiais da ANGOLAUK+.</p>
        <input
          data-testid="community-city-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("city_placeholder")}
          className="w-full px-4 py-3 rounded-xl border border-zinc-200 bg-white text-sm outline-none focus:border-black transition-colors"
        />
      </div>

      <div className="flex overflow-x-auto gap-2 pb-2 -mx-4 px-4 scrollbar-none mb-4">
        <button
          data-testid="community-city-all"
          onClick={() => setCity("")}
          className="chip"
          data-active={!cityParam}
        >
          {t("all_cities")}
        </button>
        {cities.map(c => (
          <button
            key={c.name}
            data-testid={`community-city-${c.name}`}
            onClick={() => setCity(c.name)}
            className="chip whitespace-nowrap"
            data-active={cityParam === c.name}
          >
            {c.name}
          </button>
        ))}
      </div>

      <h2 className="font-display font-bold text-base mb-2" data-testid="section-general">Geral</h2>
      {general && <GeneralCard g={general} t={t} />}

      <h2 className="font-display font-bold text-base mb-2" data-testid="section-location">Por localização</h2>
      <p className="text-xs text-zinc-500 mb-2">Comunidades oficiais ANGOLAUK+ — podes aderir a qualquer cidade.</p>
      <div className="grid grid-cols-2 gap-3 mb-6" data-testid="city-grid">
        {filteredCities.map(c => <CityCard key={c.name} c={c} onFilter={setCity} t={t} />)}
      </div>

      {women && <section className="mb-6" data-testid="section-women">
        <h2 className="font-display font-bold text-base mb-2">Angolanas em UK</h2>
        <WomenCommunityCard c={women} onChange={loadWomen} testid="communities-women-card" />
      </section>}

      {communities.length > 0 && (
        <div className="space-y-2 mb-6" data-testid="other-communities">
          <h2 className="font-display font-bold text-base">Outras comunidades</h2>
          {communities.map(g => (
            <Link key={g.id} to={`/comunidade/${g.id}`} data-testid={`community-group-${g.id}`} className="block bg-white rounded-2xl border border-zinc-200 p-3 card-hover">
              <div className="font-semibold text-sm truncate">{g.name}</div>
              <div className="text-xs text-zinc-500 flex items-center gap-2">{g.city && <span><MapPin size={11} className="inline mr-0.5" />{g.city}</span>}{showCounts && <span><Users size={11} className="inline mr-0.5" />{plural(g.members_count, t("member_one"), t("member_other"))}</span>}</div>
            </Link>
          ))}
        </div>
      )}

      <div className="mb-6"><SuggestCommunity /></div>

      {config.user_groups_enabled && <GroupsSection cityParam={cityParam} t={t} />}
    </div>
  );
}

function GroupsSection({ cityParam, t }) {
  const showCounts = useShowMemberCounts();
  const [groups, setGroups] = useState(null);
  const [cats, setCats] = useState([]);
  const [gq, setGq] = useState("");
  const [cat, setCat] = useState("");
  const [sort, setSort] = useState("members");
  useEffect(() => { api.get("/groups/categories").then(r => setCats(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const h = setTimeout(() => {
      api.get("/groups", { params: { ...(gq ? { q: gq } : {}), ...(cat ? { category: cat } : {}), ...(cityParam ? { city: cityParam } : {}), sort } })
        .then(r => setGroups(r.data)).catch(() => setGroups([]));
    }, 250);
    return () => clearTimeout(h);
  }, [gq, cat, cityParam, sort]);
  return (
    <section data-testid="groups-section">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h2 className="font-display font-bold text-base">Grupos da comunidade</h2>
      </div>
      <p className="text-xs text-zinc-500 mb-2">Espaços criados por membros — sem selo oficial. Geridos pelos próprios criadores e moderadores.</p>
      <div className="flex gap-2 mb-2">
        <input value={gq} onChange={e => setGq(e.target.value)} placeholder="Pesquisar grupos…" data-testid="groups-search" className="flex-1 px-3 py-2 rounded-xl border border-zinc-200 bg-white text-sm outline-none focus:border-black" />
        <select value={sort} onChange={e => setSort(e.target.value)} data-testid="groups-sort" className="px-2 py-2 rounded-xl border border-zinc-200 bg-white text-xs">
          <option value="members">Mais membros</option><option value="recent">Mais recentes</option><option value="name">Nome</option>
        </select>
      </div>
      <div className="flex overflow-x-auto gap-2 pb-2 -mx-4 px-4 scrollbar-none mb-3">
        <button onClick={() => setCat("")} className="chip whitespace-nowrap" data-active={!cat} data-testid="groups-cat-all">Todas</button>
        {cats.map(c => <button key={c} onClick={() => setCat(c === cat ? "" : c)} className="chip whitespace-nowrap" data-active={cat === c} data-testid={`groups-cat-${c}`}>{c}</button>)}
      </div>
      <div className="space-y-2 mb-8" data-testid="groups-list">
        {groups && groups.length === 0 && <p className="text-sm text-zinc-500 italic text-center py-8" data-testid="groups-empty">{cityParam || gq || cat ? "Nenhum grupo com estes filtros." : "Ainda não há grupos."}</p>}
        {(groups || []).map(g => (
          <Link key={g.id} to={`/grupos/${g.slug}`} data-testid={`group-card-${g.id}`} className="block bg-white rounded-2xl border border-zinc-200 p-4 card-hover">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-zinc-900 flex items-center justify-center text-white font-bold shrink-0 overflow-hidden">
                {g.cover_url ? <img src={g.cover_url} alt="" className="w-full h-full object-cover" /> : g.name.slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <div className="font-semibold text-sm truncate">{g.name}</div>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-600" data-testid={`group-category-${g.id}`}>{g.category}</span>
                  {g.privacy === "private" && <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-800 text-white" data-testid={`group-private-${g.id}`}><Lock size={9} /> Privado</span>}
                </div>
                <div className="text-xs text-zinc-500 mb-1 flex items-center gap-2 flex-wrap">
                  <span><MapPin size={11} className="inline mr-0.5" />{g.city || "Reino Unido"}</span>
                  {showCounts && <span data-testid={`group-members-${g.id}`}><Users size={11} className="inline mr-0.5" />{plural(g.members_count, t("member_one"), t("member_other"))}</span>}
                  {g.join_policy === "approval" && <span className="inline-flex items-center gap-0.5"><Lock size={10} /> aprovação</span>}
                </div>
                <p className="text-xs text-zinc-600 leading-relaxed line-clamp-2">{g.description}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
